import { Injectable, computed, signal } from '@angular/core';
import { ApiPutUserResponse, ApiUserResponse } from './api-user.service';
import { UserRole } from '../models/user-role.type';

@Injectable({ providedIn: 'root' })
export class UserStoreService {
  private readonly _actualRoles = signal<UserRole[]>([]);
  private readonly _activeOrg = signal<string | null>(null);
  private readonly _firstName = signal<string>('');
  private readonly _lastName = signal<string>('');
  private readonly _email = signal<string>('');
  private readonly _cellPhone = signal<string>('');
  private readonly _country = signal<string>('');

  firstName = this._firstName.asReadonly();
  lastName = this._lastName.asReadonly();
  email = this._email.asReadonly();
  cellPhone = this._cellPhone.asReadonly();
  country = this._country.asReadonly();

  fullName = computed(() => `${this._firstName()} ${this._lastName()}`.trim());

  // TEMPORARY, FOR TESTING ONLY: lets the sidebar's debug role switcher preview the UI as a
  // different role without changing the actual logged-in user's roles. Remove this together with
  // the sidebar debug panel once role-based UI work is verified.
  private readonly _roleOverride = signal<UserRole | null>(null);

  actualRoles = this._actualRoles.asReadonly();
  roleOverride = this._roleOverride.asReadonly();
  activeOrg = this._activeOrg.asReadonly();

  roles = computed<UserRole[]>(() => {
    const override = this._roleOverride();
    return override ? [override] : this._actualRoles();
  });

  isSuperAdmin = computed(() => this.roles().includes('SuperAdmin'));
  isAdmin = computed(() => this.isSuperAdmin() || this.roles().includes('Admin'));

  // TEMPORARY, FOR TESTING ONLY: the real logged-in role, unaffected by the debug override —
  // used to keep the role switcher itself visible/usable no matter what role is being previewed.
  isActualSuperAdmin = computed(() => this._actualRoles().includes('SuperAdmin'));

  setUserData(data: ApiUserResponse): void {
    this._actualRoles.set(data.role);
    this._activeOrg.set(data.activeOrg);
    this._firstName.set(data.firstName);
    this._lastName.set(data.lastName);
    this._email.set(data.email);
    this._cellPhone.set(data.cellPhone);
    this._country.set(data.country);
  }

  setActiveOrg(organizationId: string): void {
    this._activeOrg.set(organizationId);
  }

  setProfile(data: ApiPutUserResponse): void {
    this._firstName.set(data.firstName);
    this._lastName.set(data.lastName);
    this._email.set(data.email);
    this._cellPhone.set(data.cellPhone);
    this._country.set(data.country);
  }

  hasRole(role: UserRole): boolean {
    return this.roles().includes(role);
  }

  clear(): void {
    this._actualRoles.set([]);
    this._roleOverride.set(null);
    this._activeOrg.set(null);
    this._firstName.set('');
    this._lastName.set('');
    this._email.set('');
    this._cellPhone.set('');
    this._country.set('');
  }

  //TEMPORARY, FOR TESTING ONLY: preview the UI as a different role.
  setRoleOverride(role: UserRole | null): void {
    if(!this.isActualSuperAdmin()) return;
    this._roleOverride.set(role);
  }
}
